import { capitalize, isNil, lowerCase, upperCase } from 'lodash-es';
import dayjs from 'dayjs';
import { i18n } from '@/common/locale';
import { SelectboxItem } from '@/common/utils/types';
import { PlatformType } from '@/types/common.types';
import { ASTER_KEY } from '@/dashboard/features/widgets/common/constants/define';
import { NotificationIdMap } from '@/config/utils/alert.type';
import { TimePeriodInfo } from '@/common/components/molecules/timeController/types';
import { getCustomPeriodInterval } from '@/common/utils/chartUtils';
import { useStatInfoStore } from '@/common/stores/stat-info';
import { NotificationConfigInfo } from '@/api-client/systemAlert/model';
import {
  ALERT_TYPE_VALUE,
  CHECK_BY_TARGET,
  EVENT_TYPE_ENUM,
  getCheckHoldTimeItems,
} from '@/alert/utils/define';
import { getCheckIntervalItems } from '@/config/utils/alert.define';
import {
  RuleCriteria,
  RuleInfoProps,
  TimeParams,
} from '@/alert/components/alertDetail/alertDetail.types';
import { AlertTargetItem } from '../components/alertTargetList/alertTargetList.types';
import { compareArrayFieldValues } from '@/common/utils/core';

/**
 *  Alert Detail > get Time Period Info
 * */
export const getTimeInfoApiParams = (info: TimePeriodInfo, useInterval = false): TimeParams => {
  if (!info.isPaused) {
    return { period: info.timePeriod };
  }
  return useInterval
    ? {
        fromTime: info.fromTimeUtc,
        toTime: info.toTimeUtc,
        interval: getCustomPeriodInterval('', {
          fromTime: +dayjs(info.fromTimeUtc),
          toTime: +dayjs(info.toTimeUtc),
        }),
      }
    : {
        fromTime: info.fromTimeUtc,
        toTime: info.toTimeUtc,
      };
};

/**
 * Alert 설정에서 Selectbox 사용하는 항목들 displayName
 * @param items selectbox items
 * @param val value
 */
export const getDisplayNameFromSelectboxItem = (
  items: SelectboxItem[],
  val?: string,
  type?: 'evaluate' | 'duringTime',
) => {
  if (!val) {
    return '';
  }

  // 기존 min,max 가 저장되어 표출하려 할경우 current로 변경하여 보여준다
  if (type === 'evaluate') {
    if (['max', 'min'].includes(val)) {
      return items.find((v) => v.value === 'current')?.name ?? i18n.global.t('WORD.CURRENT');
    }
  }

  return items.find((v) => v.value === val)?.name ?? val;
};

/**
 * Alert Grid 용 column formatter
 */
export const LAST_ALERT_LEVELS = {
  CRITICAL: 'Critical',
  WARNING: 'Warning',
  NONE: 'No Data',
  NORMAL: 'Normal',
}; // Grid Filter Search 목록 제공 용
export const columnFormatter = {
  activation: (v: boolean) => (v ? 'Active' : 'Inactive'),
  alertLevel: (v: string) => LAST_ALERT_LEVELS[upperCase(v)] ?? capitalize(lowerCase(v)),
  lastAlert: (v: string) => LAST_ALERT_LEVELS[upperCase(v)] ?? capitalize(lowerCase(v)), // TODO: lastAlert 엮인 설정쪽 있어서 임시 유지 (태그)
  alert: (v: string) => (v === 'NONE' ? 'No Data' : capitalize(v)),
  value: (v: string) => v || '',
};

export const ALERT_LEVEL_ORDER: Record<string, number> = { normal: 0, warning: 1, critical: 2 };

// 알 수 없는 레벨(예: 'none', 빈 값)은 3으로 처리하여 normal(2) 이후에 정렬됨
export const alertLevelSortFn = (a: unknown, b: unknown): number =>
  (ALERT_LEVEL_ORDER[String(a).toLowerCase()] ?? 3) -
  (ALERT_LEVEL_ORDER[String(b).toLowerCase()] ?? 3);

// ModernGrid CustomColumn.sortingFn 용 래퍼 (rowA/rowB는 TanStack Row 객체)
export const alertLevelColumnSortFn = (
  rowA: { getValue: (columnId: string) => unknown },
  rowB: { getValue: (columnId: string) => unknown },
  columnId: string,
): number => alertLevelSortFn(rowA.getValue(columnId), rowB.getValue(columnId));

export const useAlertFormatter = () => {
  const statInfoStore = useStatInfoStore();

  return {
    alertName: (v: string) => {
      const statInfo = statInfoStore.getStatInfo({
        statId: v,
      });

      if (!statInfo) {
        return v;
      }
      return statInfo.name;
    },
    statUnit: (v: string) => {
      return statInfoStore.getStatInfo({
        statId: v,
      })?.unit;
    },
  };
};

export type AlertStatUnitRow = {
  alertName?: string;
  alertRuleType?: string;
  parent?: { alertRuleType?: string };
  eventType?: string;
  threshold?: {
    unit?: string;
  };
};

export const resolveAlertStatUnit = (
  row: AlertStatUnitRow,
  alertFormatter: ReturnType<typeof useAlertFormatter>,
): string => {
  const type = lowerCase(row.alertRuleType || row.parent?.alertRuleType || '');

  if (type === lowerCase(ALERT_TYPE_VALUE.METRIC)) {
    return alertFormatter.statUnit(row.alertName ?? '') ?? '';
  }

  if (type === lowerCase(ALERT_TYPE_VALUE.TABLE)) {
    return row.threshold?.unit?.trim() ?? '';
  }

  if (type === lowerCase(ALERT_TYPE_VALUE.EVENT)) {
    if (row.eventType === EVENT_TYPE_ENUM.DATABASE) {
      return '';
    }
    return row.alertName ?? '';
  }

  return '';
};

/**
 * Alert 구분
 * @param type 'System' | 나머지(user)
 */
export const getAlertType = (type: string): 'user' | 'system' | string => {
  switch (type.toLowerCase()) {
    case 'system':
      return 'system';
    case 'prometheus':
    case 'elasticsearch':
      return type;
    default:
      return 'user';
  }
};

/**
 * Alert Rule 구분
 * @param type 'metric' | 'event'
 */
export const getAlertRuleType = (type: string): 'metric' | 'event' =>
  type?.toLowerCase() === 'metric' ? 'metric' : 'event';

/**
 * System Alert > Rule Name(platformName:eventName) 분리
 * @param name platformName:eventName
 */
export const getSystemAlertInfo = (name: string) => {
  const nameDivider = name?.split(':');
  let platform = (nameDivider?.[0]?.toLowerCase() as PlatformType) ?? undefined;
  if (platform?.includes('aws') || platform?.includes('azure')) {
    platform = 'cloud';
  }
  return {
    platform,
    event: nameDivider?.[1] ?? '',
  };
};

const detailParamsController = {
  user: (name: string, ruleId?: string): RuleInfoProps => ({
    alertRuleName: name,
    alertRuleType: 'user',
    alertRuleId: ruleId,
  }),
  system: (name: string, ruleId?: string): RuleInfoProps => ({
    alertRuleName: name,
    alertRuleType: 'system',
    alertRuleId: ruleId,
  }),
};
/**
 * Alert Detail 연계 Prop 생성 - user alert / system alert 구분 위함
 * @param name ruleName
 * @param ruleId ruleId (system alert의 경우 ruleId 없어도 됨)
 * @param type 'System' | 나머지(user)
 */
export const getAlertDetailProps = ({
  alertRuleName,
  alertRuleId,
  alertRuleType,
}: {
  alertRuleName: string;
  alertRuleId?: string;
  alertRuleType: string;
}): RuleInfoProps => {
  return detailParamsController?.[getAlertType(alertRuleType)]?.(alertRuleName, alertRuleId) ?? {};
};

/**
 * User Alert Type에 따른 Conditions 컬럼명
 */
export const getConditionsNameByUserAlertType = (userAlertType: string) => {
  const apiColumnNameObj = {
    [ALERT_TYPE_VALUE.METRIC]: 'metricConditions',
    [ALERT_TYPE_VALUE.EVENT]: 'eventConditions',
    [ALERT_TYPE_VALUE.TABLE]: 'metricConditions',
    [ALERT_TYPE_VALUE.PROMETHEUS]: 'threshold',
    [ALERT_TYPE_VALUE.EVENT_STATISTICS]: 'eventConditions',
  };
  return apiColumnNameObj[userAlertType];
};

/**
 * Alert Notification Api 응답 컴포넌트에 맞게 수정
 */
export const convertNotificationForUi = (
  data: NotificationConfigInfo,
  valueKey?: string,
): NotificationIdMap => {
  const tempObj: NotificationIdMap = {};
  Object.keys(data).forEach((key) => {
    tempObj[key] = data[key]?.[valueKey ?? 'ids'] ?? [];
  });
  return tempObj;
};

export const filterDeletedTag = (item?: { tagId: string; tagName: string | null }) => {
  return item?.tagId === ASTER_KEY || !isNil(item?.tagName);
};

/**
 * By Target 알람인 경우 알람 디테일의 Current, History 탭에서 대상 타겟으로 필터링 하기 위해 사용
 * @param ruleCriteria
 * @param targets
 * @returns
 */
export const getAlertTarget = (
  ruleCriteria?: RuleCriteria,
  targets?: AlertTargetItem[],
  ruleType?: string,
): {
  alertTargetId: string | undefined;
  alertTargetName: string | undefined;
} => {
  let alertTargetId: string | undefined;
  let alertTargetName: string | undefined;

  if ((ruleCriteria === CHECK_BY_TARGET || ruleType === 'system') && targets?.length) {
    alertTargetId = targets[0]?.id ?? '';
    alertTargetName = (targets[0]?.alias || targets[0]?.name) ?? '';
  }

  return {
    alertTargetId,
    alertTargetName,
  };
};

/**
 * Logging 타입의 alert value를 파싱
 * @param value - 파싱할 logging value
 * @returns 파싱된 문자열
 */
export const parseLoggingValue = (value: string): string => {
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'string') {
      return parsed;
    }
    return parsed.message || value;
  } catch {
    return value;
  }
};

export const sortValueWithUnitAsc = (a: unknown, b: unknown): number => {
  const aStr = String(a ?? '');
  const bStr = String(b ?? '');
  if (!aStr || !bStr) return !aStr && !bStr ? 0 : aStr ? -1 : 1;

  const intervalItems = getCheckIntervalItems();

  const findUnitOrder = (str: string): number => {
    const idx = intervalItems.findIndex((item) => str.endsWith(item.name));
    return idx === -1 ? intervalItems.length : idx;
  };

  const aUnitOrder = findUnitOrder(aStr);
  const bUnitOrder = findUnitOrder(bStr);

  if (aUnitOrder !== bUnitOrder) return aUnitOrder - bUnitOrder;

  const aVal = parseFloat(aStr.replace(/,/g, '')) || 0;
  const bVal = parseFloat(bStr.replace(/,/g, '')) || 0;
  return aVal - bVal;
};

export const sortCheckHoldTimeAsc = (a: unknown, b: unknown): number => {
  const aStr = String(a ?? '');
  const bStr = String(b ?? '');

  const holdTimeItems = getCheckHoldTimeItems();

  const aTimeName = aStr.match(/\(([^)]+)\)$/)?.[1] ?? '';
  const bTimeName = bStr.match(/\(([^)]+)\)$/)?.[1] ?? '';

  const aIdx = holdTimeItems.findIndex((item) => item.name === aTimeName);
  const bIdx = holdTimeItems.findIndex((item) => item.name === bTimeName);

  const aOrder = aIdx === -1 ? holdTimeItems.length : aIdx;
  const bOrder = bIdx === -1 ? holdTimeItems.length : bIdx;

  return aOrder - bOrder;
};

export const systemAlertCustomAscFunction = {
  checkInterval: sortValueWithUnitAsc,
  duration: sortValueWithUnitAsc,
  checkHoldTime: sortCheckHoldTimeAsc,
};

export const alertTargetSortFn = (
  a: Array<{ name: string }> | null,
  b: Array<{ name: string }> | null,
): number =>
  compareArrayFieldValues(
    (a ?? []).map((item) => item.name),
    (b ?? []).map((item) => item.name),
  );
